import React from 'react';
import { Link } from '@mui/material';
import { LegalPageLayout, LegalSection } from 'components/LegalPageLayout';
import BulletList from 'components/BulletList';

// ==============================|| TERMS AND CONDITIONS ||============================== //
// Source: KiT Contact Terms and Conditions, Sterling Systems Private Limited,
// Version 1.0, effective/last updated 19 July 2026.

const ELIGIBILITY_ACCOUNTS = [
  'You must be legally competent to enter into a contract under Indian law. A minor may use the service only through a parent or lawful guardian and where the service expressly permits it.',
  'You must provide accurate information and keep it updated.',
  'You are responsible for credentials, authorised users and all activity under the account.',
  'You must promptly notify us of unauthorised access or security concerns.',
  'Business account administrators may manage users, access, retention and content within their organisation, subject to their own legal obligations.'
];

const PROHIBITED_CONDUCT = [
  'Breaking any law, privacy right, intellectual-property right or contractual duty.',
  'Uploading malware, unlawful content, stolen data or information obtained without authority.',
  'Sending spam, unsolicited bulk messages, harassment or deceptive communications.',
  'Reverse engineering, bypassing security or usage limits, scraping, probing or disrupting the service, except where law expressly permits.',
  'Reselling, sublicensing or providing access outside an authorised plan.',
  'Using the service for surveillance, discrimination, unlawful profiling or activities that create material risk to another person.'
];

const TermsAndConditions = () => (
  <LegalPageLayout title="Terms and Conditions" lastUpdated="19 July 2026">
    <LegalSection title="1. Acceptance of Terms">
      These Terms and Conditions (&quot;Terms&quot;) form a binding agreement between the person or entity using KiT
      Contact (&quot;you&quot; or &quot;User&quot;) and Sterling Systems Private Limited. By creating an account,
      purchasing a subscription, clicking acceptance or using the service, you confirm that you have read and
      accepted these Terms and the Privacy Policy. If you use the service for an organisation, you represent that you
      are authorised to bind that organisation.
    </LegalSection>

    <LegalSection title="2. Service Description">
      KiT Contact is a contact and business relationship management platform that helps users organise contacts,
      business connections, notes, categories, reminders, interactions and related relationship information across
      supported devices and web interfaces. Features may vary by plan, platform, device, operating-system version,
      region and release. We may improve, modify, discontinue or replace features, while respecting prepaid
      commitments and mandatory consumer rights.
    </LegalSection>

    <LegalSection title="3. Eligibility and Accounts">
      <BulletList items={ELIGIBILITY_ACCOUNTS} />
    </LegalSection>

    <LegalSection title="4. Licence and Permitted Use">
      Subject to payment and compliance with these Terms, Sterling Systems grants a limited, non-exclusive,
      non-transferable, revocable right to access and use the service for personal or internal business purposes
      during the subscription or authorised access period. No ownership in the software, source code, designs,
      trademarks or documentation is transferred.
    </LegalSection>

    <LegalSection title="5. User Data and Lawful Authority">
      You retain ownership of information you lawfully submit. You grant Sterling Systems and its approved processors
      a limited licence to host, copy, transmit, display, back up and otherwise process that information solely to
      provide, secure, support and improve the service and comply with law.
      <br />
      <br />
      You represent that you have provided all required notices and obtained all necessary consents, permissions or
      other lawful authority before entering personal data of contacts, callers, employees, customers or other
      individuals. You are responsible for responding to those individuals when you determine the purpose and means
      of processing.
    </LegalSection>

    <LegalSection title="6. Prohibited Conduct">
      <BulletList items={PROHIBITED_CONDUCT} />
      Users must have a lawful basis and necessary permission to upload or process personal data relating to other
      individuals. KiT Contact is not intended to be used for unsolicited marketing, harassment, unlawful profiling,
      sale of personal data, or any activity prohibited by law.
    </LegalSection>

    <LegalSection title="7. Subscriptions, Fees, Taxes and Renewals">
      Paid plans, billing cycles and included limits are displayed at purchase. Fees are payable in advance unless
      otherwise agreed and may include applicable taxes. Recurring subscriptions may renew automatically only where
      the checkout and platform rules clearly disclose and authorise renewal. You may cancel future renewal through
      the relevant app store, payment provider or account settings. Cancellation ordinarily stops future charges but
      does not erase data or generate a refund unless stated in the applicable refund policy or required by law.
    </LegalSection>

    <LegalSection title="8. Third-Party Services">
      The service may integrate with operating-system features, app stores, payment gateways, cloud services or
      external links. Third-party services are governed by their own terms and privacy policies. We are not
      responsible for third-party services beyond obligations imposed by law, but we will use reasonable care in
      selecting processors that handle service data.
    </LegalSection>

    <LegalSection title="9. Availability, Support and Updates">
      We aim to provide reliable access but do not guarantee uninterrupted or error-free operation. Planned
      maintenance, network failures, device restrictions, force majeure, security events or third-party outages may
      affect service. Support is available through email by writing to us at{' '}
      <Link href="mailto:info@sterlingsys.in">info@sterlingsys.in</Link>. Users must install critical updates and
      maintain supported devices and connectivity.
    </LegalSection>

    <LegalSection title="10. Intellectual Property">
      The service, software, interface, databases, documentation, brand names, logos and all related intellectual
      property, excluding User Data and third-party materials, belong to Sterling Systems Private Limited or its
      licensors. Feedback may be used without restriction or payment, provided we do not identify the user publicly
      without permission.
    </LegalSection>

    <LegalSection title="11. Suspension and Termination">
      We may suspend or terminate access for non-payment, material breach, security risk, unlawful use, abuse, legal
      requirement or conduct that may harm users or the service. Where reasonably possible, we will provide notice
      and an opportunity to remedy. You may stop using the service and request account deletion. Provisions
      concerning fees due, intellectual property, confidentiality, liability, dispute resolution and lawful retention
      survive termination.
    </LegalSection>

    <LegalSection title="12. Disclaimers">
      The service is a software tool and does not provide legal, financial, medical or professional advice. Except
      for warranties that cannot legally be excluded, the service is provided on an &quot;as available&quot; basis.
      You are responsible for reviewing outputs, maintaining lawful source records and keeping independent exports or
      backups appropriate to your business continuity needs.
    </LegalSection>

    <LegalSection title="13. Limitation of Liability">
      To the maximum extent permitted by law, neither party will be liable for indirect, incidental, special,
      punitive or consequential loss, including lost profits, goodwill or business opportunity. Sterling Systems’
      aggregate liability arising from the service will not exceed the fees paid by the affected customer for the
      service during the twelve months immediately preceding the event giving rise to the claim. This limit does not
      apply to liability that cannot lawfully be limited, fraud, wilful misconduct, infringement obligations
      expressly accepted by contract, or breach of confidentiality or data-protection obligations to the extent
      exclusion is prohibited by law.
    </LegalSection>

    <LegalSection title="14. Indemnity">
      To the extent permitted by law, a business user will indemnify Sterling Systems against third-party claims
      arising from unlawful User Data, failure to obtain required authority, prohibited communications, infringement
      by user-supplied content or material breach of these Terms. Sterling Systems will provide reasonable notice and
      control of defence, subject to protection of its legitimate interests.
    </LegalSection>

    <LegalSection title="15. Consumer Rights">
      Nothing in these Terms excludes rights available under the Consumer Protection Act, 2019 or other mandatory
      law. In case of inconsistency, the mandatory consumer protection provision will prevail.
    </LegalSection>

    <LegalSection title="16. Governing Law, Disputes and Pune Jurisdiction">
      These Terms are governed by the laws of India. The parties should first attempt good-faith resolution by
      written notice to the grievance contact. Subject to mandatory consumer forums, the Data Protection Board of
      India, statutory appellate remedies, or any other forum that cannot legally be excluded, courts of competent
      jurisdiction in Pune, Maharashtra shall have exclusive jurisdiction.
    </LegalSection>

    <LegalSection title="17. General">
      We may assign these Terms as part of a merger, restructuring or transfer of the service. You may not assign
      them without written consent. If any provision is unenforceable, it will be limited or severed without
      affecting the remainder. Failure to enforce a provision is not a waiver. Notices may be delivered through the
      account, email, app, website or registered address.
    </LegalSection>

    <LegalSection title="18. Contact">
      Sterling Systems Private Limited, Pratima, First Floor, Market Yard Road, Adinanth Society, Maharshi Nagar, Pune,
      Maharashtra&ndash; 411037, INDIA.
      <br />
      Support/Privacy/grievance: <Link href="mailto:info@sterlingsys.in">info@sterlingsys.in</Link>
    </LegalSection>
  </LegalPageLayout>
);

export default TermsAndConditions;
